import { SideModal } from '@design-system/components/modal/sideModal';
import {
  UseEditVideoVMParams,
  useEditVideoVM,
} from '../view-models/editVideo.viewModel';
import { EditVideoView } from './editVideo.view';
import { Tag } from '@design-system/components/tag/tag';
import { Modal } from '@design-system/components/modal/modal';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { useEffect, useRef } from 'react';
import { allowedVideoTypes } from '../constants';

type EditVideoModalParams = UseEditVideoVMParams;

const warnBeforeUnload = (ev: BeforeUnloadEvent) => {
  ev.preventDefault();
  ev.returnValue = '';
};

export const EditVideoModal = (p: EditVideoModalParams) => {
  const vm = useEditVideoVM(p);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!vm.isReplacing) return;
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [vm.isReplacing]);

  const pickReplacement = () => {
    vm.dispatch('closeConfirmation');
    fileInputRef.current?.click();
  };

  const onReplacementPicked = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (file) vm.dispatch('replaceUpload', file);
  };

  return (
    <>
      <input
        type="file"
        accept={allowedVideoTypes.join(',')}
        ref={fileInputRef}
        onChange={onReplacementPicked}
        hidden
      />
      <SideModal
        isOpen={true}
        onClose={() => vm.dispatch('requestClose')}
        title={p.video.name}
        titleClassName="text-ellipsis max-w-[500px] whitespace-nowrap overflow-hidden"
        headerComponents={<Tag label={vm.tagLabel} />}
        primaryCta={{
          caption: 'Save',
          onClick: () => vm.dispatch('submitEdit'),
          state: vm.isReplacing ? 'disabled' : undefined,
        }}
        drawerSx={{
          '&.MuiDrawer-root > .MuiDrawer-paper': {
            width: { lg: 850 },
            paddingX: '36px',
            overflow: 'auto',
          },
        }}
      >
        <EditVideoView {...vm} />
      </SideModal>
      <Modal
        isOpen={vm.showReplaceConfirmation}
        onClose={() => vm.dispatch('closeConfirmation')}
        title="Replacing file?"
        disableBackdropClick={true}
        modalClassName="w-full md:w-[450px]"
      >
        <div className="flex flex-col gap-6">
          <Typography variant="body" size="large">
            Replace the file with a new one from your device.
          </Typography>
          <Typography variant="body" size="large">
            This action cannot be undone, and changes at the library level won't
            affect files already in workouts.
          </Typography>
          <div className="flex flex-col md:flex-row justify-between">
            <Button
              onClick={() => vm.dispatch('closeConfirmation')}
              color="neutral"
              variant="text"
              className="w-full md:w-[200px]"
            >
              Cancel
            </Button>
            <Button
              onClick={pickReplacement}
              color="neutral"
              className="w-full md:w-[200px]"
            >
              Proceed
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
